import './default.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import PaidAlertModal from '@/resources/components/cart/paidAlertModal/PaidAlertModal';
import SendOrderAlert from '@/resources/components/cart/sendOrderAlert/SendOrderAlert';
import Shipment from '@/resources/components/cart/shipment/Shipment';
import StatusSteps from '@/resources/components/cart/statusSteps/StatusSteps';
import CartPageLoad from '@/resources/components/loads/CartPageLoad';
import { useBeginCheckout } from '@/resources/hooks/useBeginCheckout';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { useInitiateCheckoutPixel } from '@/resources/hooks/useInitiateCheckoutPixel';
import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { usePageBreakpoints } from '@/resources/hooks/usePageBreakpoints';
import { useSmartTextColor } from '@/resources/hooks/useSmartTextColor';
import { useTextColor } from '@/resources/hooks/useTextColor';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { ApplicationState } from '@/store';
import { IPaymentAlertAsks } from '@/store/ducks/payment/types';

import { canChangeOrder, canFinishOrder, OrderStatus } from '../../../../services/global';
import { CartContent, CheckoutNavigation, EmptyCart } from '../../cart/default/components';
import { CartContextStepper } from '../../cart/default/components/CartContextStepper';
import { FinishedOrder } from '../../cart/default/components/FinishedOrder';
import { useCartData, useCartEffects, useCartModals } from '../../cart/default/hooks';
import { shouldShowStatusSteps } from '../../cart/default/utils/cartUtils';
import { ComboAddModal } from '../../product/default/combos/components/ComboAddModal';
import { ComboBottomSheet } from '../../product/default/combos/components/ComboBottomSheet';
import { ComboModal } from '../../product/default/combos/components/ComboModal';
import { ComboRemoveItemModal } from '../../product/default/combos/components/ComboRemoveItemModal';

function Default() {
  const color = useSmartTextColor();
  const { breakpoints } = usePageBreakpoints();
  const { configs, setShowIsCartBlockedModal, order, menuOpen } = useWebContext();
  const history = useHistory();

  const redirectIfOrderClosed = useMemo(() => [OrderStatus.CLOSED, OrderStatus.CANCELED], []);

  const validPayments = order?.payments
    ?.filter((p) => !p.deleted_at)
    .filter((p, index, arr) => {
      const next = arr[index + 1];
      const prev = arr[index - 1];

      const isSameAsNext = next && p.total_paid === next.total_paid;
      const prevSumExceeds = prev && p.total_paid + prev.total_paid > order?.total;

      return !(isSameAsNext && prevSumExceeds);
    });

  const { setFunction } = useOrderContext();

  const textColor = useTextColor(configs.color);
  const { min_order_amount } = useCartContext();

  const [shipmentStep, setShipmentStep] = useState<1 | 2 | 3 | 4>(1);
  const [showSendOrderAlert, setShowSendOrderAlert] = useState<boolean>(false);
  const [showCartDeliveryAlert, setShowCartDeliveryAlert] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<'checkout' | 'finished'>('checkout');
  const [showSteps, setShowSteps] = useState<boolean>(false);
  const [isOrderFirstChange, setIsOrderFirstChange] = useState<boolean>(true);

  const cartModals = useCartModals();
  const cartData = useCartData();

  const paymentAlertAsks = useSelector<ApplicationState, IPaymentAlertAsks>(
    (state) => state.payment.asks,
  );

  const address = order?.shipment?.address?.id;
  const type = order?.shipment?.type;

  useEffect(() => {
    if (order) {
      useInitiateCheckoutPixel(order);
      useBeginCheckout(order);
    }
  }, [order?.id]);

  useEffect(() => {
    if (order && redirectIfOrderClosed.includes(order.status)) {
      window.location.href = '/';
    }
  }, [order, redirectIfOrderClosed]);

  useEffect(() => {
    if (!isOrderFirstChange) {
      history.push('/cart');
    }
    if (order) {
      setIsOrderFirstChange(false);
    }
  }, [address, type]);

  useCartEffects({
    order,
    paymentAlertAsks,
    setShowCartDeliveryAlert,
  });

  useEffect(() => {
    if (validPayments?.some((p) => p.status === 'paid') || order?.is_paid) {
      setCurrentView('finished');
    }
  }, [order, validPayments]);

  useEffect(() => {
    if (!menuOpen && cartModals.comboBottomSheetOpen) {
      cartModals.closeComboBottomSheet();
    }
  }, [menuOpen, cartModals.comboBottomSheetOpen]);

  useEffect(() => {
    if (cartData.productVariants && Object.keys(cartData.productVariants).length > 0) {
      cartModals.openComboDetails();
    }
  }, [cartData.productVariants]);

  const shipmentCardStyle = {
    backgroundColor: configs.color,
    borderColor: configs.color,
    color: textColor,
  };

  const handleCloseSendOrderAlert = useCallback(
    () => setShowSendOrderAlert(false),
    [setShowSendOrderAlert],
  );

  const handleFinishOrder = useCallback(() => {
    const { isValid: orderCanBeChanged, showIsCartBlockedModal } = canChangeOrder({
      type: 'pedido',
      action: 'finalizado',
    });

    if (
      (order?.status !== OrderStatus.OPEN && order?.status !== OrderStatus.ABANDONED,
      order?.cart.products.some((item: any) => item.prescription_retention))
    ) {
      return true;
    }

    if (orderCanBeChanged && canFinishOrder(shipmentStep)) return true;

    if (showIsCartBlockedModal) setShowIsCartBlockedModal(true);

    return false;
  }, [
    order?.status,
    order?.cart.products,
    canChangeOrder,
    canFinishOrder,
    shipmentStep,
    setShowIsCartBlockedModal,
  ]);

  const handleCloseComboModal = useCallback(() => {
    cartModals.closeComboModal();
    cartData.clearProductVariants();
  }, [cartModals, cartData]);

  const handleCloseComboBottomSheet = useCallback(() => {
    cartModals.closeComboBottomSheet();
    cartData.clearProductVariants();
  }, [cartModals, cartData]);

  const backToCart = useCallback(() => {
    history.push('/cart');
  }, [history]);

  const proceedToFinished = useCallback(() => {
    setCurrentView('finished');
  }, []);

  const shouldShowSteps = useCallback(() => {
    setShowSteps(true);
  }, []);

  useEffect(() => {
    setFunction('handleFinishOrder', handleFinishOrder);
  }, [setFunction, handleFinishOrder]);

  const forwardedOrderAlertCallback = useCallback(
    (show: boolean) => setShowSendOrderAlert(show),
    [setShowSendOrderAlert],
  );

  useEffect(() => {
    setFunction('forwardedOrderAlert', forwardedOrderAlertCallback);
  }, [setFunction, forwardedOrderAlertCallback]);

  useEffect(() => {
    setFunction('backToCart', backToCart);
    setFunction('proceedToFinished', proceedToFinished);
    setFunction('shouldShowSteps', shouldShowSteps);
  }, [setFunction, backToCart, proceedToFinished]);

  return (
    <section className="Checkout pb-3" style={{ overflowX: 'hidden' }}>
      <Container fluid>
        <Row className="justify-content-center">
          <Col {...breakpoints} className="breakpoint-page">
            {order === null ? (
              <CartPageLoad />
            ) : (
              <>
                {order && order.cart.products.length > 0 ? (
                  <>
                    {shouldShowStatusSteps(order, showSteps) && (
                      <Row className="justify-content-center my-2">
                        <Col xs={12} md={10} lg={8}>
                          <StatusSteps order={order} />
                        </Col>
                      </Row>
                    )}

                    <Row className="justify-content-center">
                      <div className="text-center pt-3">
                        {!shouldShowStatusSteps(order, showSteps) && (
                          <CartContextStepper choosenStep="checkout" />
                        )}
                      </div>
                    </Row>

                    {currentView === 'checkout' && !shouldShowStatusSteps(order, showSteps) && (
                      <Row className="justify-content-center">
                        <Col xs={12}>
                          <div className="text-center my-3">
                            <CheckoutNavigation />
                          </div>
                        </Col>
                      </Row>
                    )}

                    <Row className="justify-content-center">
                      {currentView === 'checkout' ? (
                        <>
                          <Col xs={12} md={8} className="mb-3">
                            <CartContent
                              shipmentCardStyle={shipmentCardStyle}
                              showCartDeliveryAlert={showCartDeliveryAlert}
                              order={order}
                              min_order_amount={min_order_amount}
                              combos={cartData.combos}
                              setEditProducts={cartData.setEditProducts}
                              setProductToCombo={cartData.setProductToCombo}
                            />
                          </Col>
                          <Col xs={12} md={4}>
                            <Shipment
                              order={order}
                              setShipmentStep={setShipmentStep}
                              handleFinishOrder={handleFinishOrder}
                              forwardedOrderAlert={setShowSendOrderAlert}
                              showPayment={true}
                            />
                          </Col>
                        </>
                      ) : (
                        <Col xs={12}>
                          <FinishedOrder />
                        </Col>
                      )}
                    </Row>

                    <Row>
                      <Col xs={12}>
                        <SendOrderAlert
                          show={showSendOrderAlert}
                          handleClose={handleCloseSendOrderAlert}
                          socialNetworkType={order.client.social_network_type}
                          clientIdentification={order.client.identification}
                        />
                      </Col>

                      <Col xs={12}>
                        <PaidAlertModal />
                      </Col>
                    </Row>
                  </>
                ) : (
                  <EmptyCart color={color} />
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>

      <ComboBottomSheet
        open={cartModals.comboBottomSheetOpen}
        product={cartData.productToCombo}
        variants={cartData.productVariants}
        onClose={handleCloseComboBottomSheet}
        editProducts={cartData.editProducts}
      />
      <ComboModal
        show={cartModals.comboModalOpen}
        onClose={handleCloseComboModal}
        product={cartData.productToCombo}
        variants={cartData.productVariants}
        editProducts={cartData.editProducts}
      />
      <ComboAddModal
        show={cartModals.showComboAddModal}
        onClose={cartModals.closeComboAddModal}
        product={cartModals.modalAddProduct}
      />
      <ComboRemoveItemModal
        show={cartModals.comboRemoveItemModalOpen}
        onClose={cartModals.closeComboRemoveModal}
        product={cartModals.modalRemoveProduct}
      />
    </section>
  );
}

export default Default;
